// Docs: IMPLEMENTATION.md §3.4 (navigation components); FEATURES.md
// "Daily study loop". Coach shell (<768px): 3 tabs + the raised center ▶.
// The center button is state-driven via useNextLesson():
//   ▶ continue/start the next due lesson · 🗓 plan the day · ▶ bonus on green
//   (plan done: your newest extra lesson, else a Nebenspur with cards, else
//   the Erstellen window) · ▶ Heute (plan unreadable, e.g. no API key).
//   Long-press opens the Erstellen window on Heute.
// Hidden entirely while a lesson is running (html[data-lesson-active]).
import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { coachTabs, coachCenterSections, isInSection, isNavItemActive } from '../utils/navItems';
import { creatorPath } from '../utils/lessonRoutes';
import { useNextLesson } from '../hooks/useNextLesson';
import NavIcon from './NavIcons';
import { trackEvent } from '../analytics';

const CENTER_ICON = { continue: 'play', start: 'play', plan: 'kalender', setup: 'einstellungen', done: 'play', unavailable: 'play' };
const CENTER_LABEL = { continue: 'üben', start: 'üben', plan: 'planen', setup: 'einrichten', done: 'bonus', unavailable: 'üben' };

const LONG_PRESS_MS = 500;

const CoachTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const barRef = useRef(null);
  const nextLesson = useNextLesson();

  // Bibliothek spans the word list, its sub-pages and the Entdecken hub;
  // Profil spans Statistik and Einstellungen (see coachTabs.sections).
  const isActive = (item) => isNavItemActive(item, location.pathname);
  const centerActive = coachCenterSections.some((s) => isInSection(location.pathname, s));

  // Keep --app-bottom-nav-height in sync (consumed by .main padding and the
  // drill view's viewport math).
  useEffect(() => {
    if (!barRef.current) return;

    const updateHeight = () => {
      const height = Math.ceil(barRef.current.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty('--app-bottom-nav-height', `${height}px`);
    };

    updateHeight();

    let observer;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(() => {
        window.requestAnimationFrame(updateHeight);
      });
      observer.observe(barRef.current);
    }

    return () => {
      document.documentElement.style.setProperty('--app-bottom-nav-height', '0px');
      if (observer) observer.disconnect();
    };
  }, []);

  // Long-press on ▶ → make a lesson of your own instead of the one-tap action.
  const pressTimer = useRef(null);
  const longPressed = useRef(false);
  const startPress = () => {
    longPressed.current = false;
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      trackEvent('nav_center_button_long_press', { mode: nextLesson.mode });
      navigate(creatorPath());
    }, LONG_PRESS_MS);
  };
  const endPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
  };
  const handleCenterClick = () => {
    if (longPressed.current) return; // long-press already navigated
    trackEvent('nav_center_button_click', { mode });
    nextLesson.act();
  };

  const [leftTab, ...rightTabs] = coachTabs;
  const mode = nextLesson.mode;
  // An extra lesson after the plan is done plays like a lesson but stays green.
  const bonus = nextLesson.bonus;

  // Long-press on the profil tab opens the debug window (the mobile shell
  // has no other debug entry point besides Profil → erweitert).
  const profilTimer = useRef(null);
  const profilLongPressed = useRef(false);
  const startProfilPress = () => {
    profilLongPressed.current = false;
    profilTimer.current = setTimeout(() => {
      profilLongPressed.current = true;
      window.dispatchEvent(new Event('debug:open'));
    }, LONG_PRESS_MS);
  };
  const endProfilPress = () => {
    if (profilTimer.current) clearTimeout(profilTimer.current);
  };

  const renderTab = (item) => {
    const isProfil = item.path === '/profile';
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`glass-bottom-tab ${isActive(item) ? 'glass-bottom-tab--active' : ''}`}
        aria-current={isActive(item) ? 'page' : undefined}
        onPointerDown={isProfil ? startProfilPress : undefined}
        onPointerUp={isProfil ? endProfilPress : undefined}
        onPointerLeave={isProfil ? endProfilPress : undefined}
        onContextMenu={isProfil ? (e) => e.preventDefault() : undefined}
        onClick={(e) => {
          if (isProfil && profilLongPressed.current) {
            e.preventDefault();
            return;
          }
          trackEvent('nav_tab_click', { tab: item.path, shell: 'coach' });
        }}
      >
        <NavIcon name={item.icon} />
        <span className="text-[11px] font-semibold leading-tight">{item.label}</span>
      </Link>
    );
  };

  return (
    <nav ref={barRef} className="glass-bottom-nav md:hidden">
      {renderTab(leftTab)}

      <button
        type="button"
        className={`coach-center coach-center--${mode} ${bonus ? 'coach-center--bonus' : ''} ${centerActive ? 'coach-center--current' : ''}`}
        aria-current={centerActive ? 'page' : undefined}
        onClick={handleCenterClick}
        onPointerDown={startPress}
        onPointerUp={endPress}
        onPointerLeave={endPress}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={
          mode === 'plan'
            ? 'Tag planen'
            : mode === 'setup'
              ? 'Training einrichten'
            : mode === 'done'
              ? 'Tagesplan erledigt — Bonus üben'
              : mode === 'unavailable'
                ? 'Üben'
                : nextLesson.sidetrack
                  ? `Bonus üben: Nebenspur ${nextLesson.sidetrack.label}`
                  : `${bonus ? 'Bonus üben' : 'Üben'}: ${nextLesson.lesson?.name || 'nächste Lektion'}`
        }
      >
        <b>
          <NavIcon name={CENTER_ICON[mode]} size={22} />
        </b>
        <span className="text-[11px] font-semibold leading-tight">
          {bonus ? 'bonus' : CENTER_LABEL[mode]}
        </span>
      </button>

      {rightTabs.map(renderTab)}
    </nav>
  );
};

export default CoachTabBar;
